export interface NextSessionRecurringLike {
  id: string;
  weekday: number;
  startTime: string | null;
  endTime: string | null;
  title?: string | null;
}

export interface NextSessionNightLike {
  id: string;
  sessionId: string | null;
  startsAt: Date | string;
  title?: string | null;
}

export interface NextSessionResult {
  source: "night" | "recurring";
  recurringSessionId: string | null;
  nightId: string | null;
  title: string | null;
  localDate: string;
  localTime: string;
  startsAt: Date;
}

interface ResolveNextSessionOptions {
  recurringSessions: NextSessionRecurringLike[];
  upcomingNights?: NextSessionNightLike[];
  recurringWeekdayBase: "sunday-0" | "monday-1";
  now?: Date;
  includeUnlinkedNights?: boolean;
}

interface LocalDateTimeParts {
  year: number;
  month: number;
  day: number;
  weekdaySundayZero: number;
  hour: number;
  minute: number;
  second: number;
}

function parseTimeToSeconds(time: string | null | undefined): number {
  if (!time) return 0;
  const [hh, mm, ss] = time.split(":");
  return Number(hh ?? 0) * 3600 + Number(mm ?? 0) * 60 + Number(ss ?? 0);
}

function formatYmd(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function addDaysYmd(ymd: string, days: number): string {
  const [year, month, day] = ymd.split("-").map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day + days));
  return formatYmd(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
}

function localDateTimeParts(date: Date): LocalDateTimeParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    weekdaySundayZero: date.getDay(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  };
}

function localDateAndSeconds(date: Date): { ymd: string; seconds: number } {
  const local = localDateTimeParts(date);
  return {
    ymd: formatYmd(local.year, local.month, local.day),
    seconds: local.hour * 3600 + local.minute * 60 + local.second,
  };
}

function normalizeWeekday(weekday: number, base: "sunday-0" | "monday-1"): number {
  if (base === "sunday-0") return ((weekday % 7) + 7) % 7;
  return ((weekday % 7) + 7) % 7;
}

function recurringWeekdayToSundayZero(weekday: number, base: "sunday-0" | "monday-1"): number {
  if (base === "sunday-0") return normalizeWeekday(weekday, base);
  const normalized = normalizeWeekday(weekday, base);
  return normalized % 7;
}

function parseRecurringCandidate(
  session: NextSessionRecurringLike,
  recurringWeekdayBase: "sunday-0" | "monday-1",
  now: Date,
): NextSessionResult | null {
  const nowLocal = localDateTimeParts(now);
  const nowYmd = formatYmd(nowLocal.year, nowLocal.month, nowLocal.day);
  const nowSeconds = nowLocal.hour * 3600 + nowLocal.minute * 60 + nowLocal.second;
  const targetWeekday = recurringWeekdayToSundayZero(session.weekday, recurringWeekdayBase);
  const startSeconds = parseTimeToSeconds(session.startTime);
  let daysUntil = (targetWeekday - nowLocal.weekdaySundayZero + 7) % 7;

  if (daysUntil === 0 && startSeconds <= nowSeconds) {
    daysUntil = 7;
  }

  const targetYmd = addDaysYmd(nowYmd, daysUntil);
  const [year, month, day] = targetYmd.split("-").map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = (session.startTime ?? "00:00:00").split(":").map(Number);
  const startsAt = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));

  return {
    source: "recurring",
    recurringSessionId: session.id,
    nightId: null,
    title: session.title ?? null,
    localDate: targetYmd,
    localTime: session.startTime ?? "",
    startsAt,
  };
}

function parseNightCandidate(night: NextSessionNightLike): NextSessionResult {
  const startsAt = new Date(night.startsAt);
  const local = localDateAndSeconds(startsAt);

  return {
    source: "night",
    recurringSessionId: night.sessionId,
    nightId: night.id,
    title: night.title ?? null,
    localDate: local.ymd,
    localTime: `${String(Math.floor(local.seconds / 3600)).padStart(2, "0")}:${String(
      Math.floor((local.seconds % 3600) / 60),
    ).padStart(2, "0")}:00`,
    startsAt,
  };
}

function candidateSortKey(candidate: NextSessionResult): string {
  return `${candidate.localDate}T${candidate.localTime || "00:00:00"}`;
}

export function resolveNextSession({
  recurringSessions,
  upcomingNights = [],
  recurringWeekdayBase,
  now = new Date(),
  includeUnlinkedNights = false,
}: ResolveNextSessionOptions): NextSessionResult | null {
  const nowLocal = localDateAndSeconds(now);
  const nowKey = `${nowLocal.ymd}T${String(Math.floor(nowLocal.seconds / 3600)).padStart(2, "0")}:${String(
    Math.floor((nowLocal.seconds % 3600) / 60),
  ).padStart(2, "0")}:00`;

  const recurringSessionIds = new Set(recurringSessions.map((session) => session.id));
  const recurringCandidates = recurringSessions
    .map((session) => parseRecurringCandidate(session, recurringWeekdayBase, now))
    .filter((candidate): candidate is NextSessionResult => candidate !== null);
  const nightCandidates = upcomingNights
    .filter((night) => {
      if (includeUnlinkedNights) return true;
      if (!night.sessionId) return false;
      return recurringSessionIds.has(night.sessionId);
    })
    .map((night) => parseNightCandidate(night))
    .filter((candidate) => candidateSortKey(candidate) > nowKey);

  const allCandidates = [...nightCandidates, ...recurringCandidates].sort((a, b) =>
    candidateSortKey(a).localeCompare(candidateSortKey(b)),
  );
  return allCandidates[0] ?? null;
}

export function formatNextSessionDateLabel(
  nextSession: Pick<NextSessionResult, "localDate">,
  locale = "en-NZ",
): string {
  const [year, month, day] = nextSession.localDate.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export function formatNextSessionTimeLabel(
  nextSession: Pick<NextSessionResult, "localTime">,
  locale = "en-NZ",
): string {
  if (!nextSession.localTime) return "";
  const [hours, minutes] = nextSession.localTime.split(":").map(Number);
  const date = new Date(Date.UTC(2000, 0, 1, hours, minutes, 0));
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
}
